/**
 * @fileoverview Type definitions for Knowledge API endpoints
 * @description Defines request/response schemas, error types, and validation schemas for knowledge endpoints
 * @author AgentSphere
 * @since 2024-12-09
 */

import { z } from 'zod'
import type { KnowledgeRecord, UserRecord, KnowledgeContentRecord } from '@taqat/db'

/**
 * Knowledge error types for consistent error handling
 */
export enum KnowledgeErrorType {
  WORKSPACE_NOT_FOUND = 'WORKSPACE_NOT_FOUND',
  INVALID_WORKSPACE_ACCESS = 'INVALID_WORKSPACE_ACCESS',
  KNOWLEDGE_NOT_FOUND = 'KNOWLEDGE_NOT_FOUND',
  UNAUTHORIZED = 'UNAUTHORIZED',
  DELETION_FAILED = 'DELETION_FAILED',
  DATABASE_ERROR = 'DATABASE_ERROR',
  VALIDATION_ERROR = 'VALIDATION_ERROR'
}

/**
 * Knowledge error class for structured error handling
 */
export class KnowledgeError extends Error {
  constructor(
    public type: KnowledgeErrorType,
    public message: string,
    public statusCode: number = 500
  ) {
    super(message)
    this.name = 'KnowledgeError'
  }
}

/**
 * Query parameters schema for GET /api/knowledge
 * Supports pagination, filtering, and sorting
 */
export const knowledgeQueryParamsSchema = z.object({
  // Pagination
  page: z.coerce.number().min(1).optional().default(1),
  limit: z.coerce.number().min(1).max(100).optional().default(20),
  
  // Filtering
  q: z.string().optional(), // Search in title/content/metadata
  type: z.string().optional(), // e.g., document, link, file, note
  createdBy: z.string().uuid().optional(), // User ID
  from: z.string().datetime().optional(), // ISO date string
  to: z.string().datetime().optional(), // ISO date string
  tags: z.string().optional(), // Comma-separated tags
  
  // Sorting
  sort: z.string().optional().default('createdAt:desc'), // e.g., 'createdAt:desc', 'title:asc'
  
  // Optional workspace override (for multi-workspace access)
  workspaceId: z.string().uuid().optional()
})

/**
 * Type for validated query parameters
 */
export type KnowledgeQueryParams = z.infer<typeof knowledgeQueryParamsSchema>

/**
 * Sort field and direction type
 */
export type SortConfig = {
  field: string
  direction: 'asc' | 'desc'
}

/**
 * Enhanced knowledge record with user details for API responses
 */
export type KnowledgeWithUser = KnowledgeRecord & {
  user: UserRecord | null
  knowledgeContent: KnowledgeContentRecord[]
}

/**
 * Pagination metadata for responses
 */
export type PaginationMeta = {
  total: number
  page: number
  limit: number
  totalPages: number
  hasNextPage: boolean
  hasPrevPage: boolean
}

/**
 * Success response for knowledge list endpoint
 */
export type KnowledgeListResponse = {
  success: true
  data: {
    knowledge: KnowledgeWithUser[]
    pagination: PaginationMeta
  }
  message: string
}

/**
 * Error response structure
 */
export type KnowledgeErrorResponse = {
  success: false
  error: {
    type: KnowledgeErrorType
    message: string
    details?: string
  }
}

/**
 * Union type for all possible knowledge API responses
 */
export type KnowledgeApiResponse = KnowledgeListResponse | KnowledgeErrorResponse

/**
 * Filters configuration for knowledge queries
 */
export type KnowledgeFilters = {
  searchQuery?: string
  type?: string
  createdBy?: string
  dateFrom?: Date
  dateTo?: Date
  tags?: string[]
}

/**
 * Knowledge service configuration for queries
 */
export type KnowledgeQueryConfig = {
  workspaceId: string
  pagination: {
    page: number
    limit: number
    offset: number
  }
  filters: KnowledgeFilters
  sort: SortConfig
}

/**
 * Transform knowledge record for API response
 * Ensures consistent data structure and removes sensitive information
 */
export function transformKnowledgeForResponse(knowledge: KnowledgeWithUser): KnowledgeWithUser {
  return {
    ...knowledge,
    // Remove sensitive user information
    user: knowledge.user ? {
      ...knowledge.user,
      password: null,
      passwordSalt: null,
      tempPassword: null,
    } as UserRecord : null
  }
}

/**
 * Parse sort parameter into structured sort configuration
 * @param sortParam - Sort parameter string (e.g., 'createdAt:desc', 'title:asc')
 * @returns Parsed sort configuration
 */
export function parseSortParam(sortParam: string): SortConfig {
  const [field = 'createdAt', direction = 'desc'] = sortParam.split(':')
  
  // Validate sort field
  const validFields = ['createdAt', 'updatedAt', 'title', 'type']
  const validField = validFields.includes(field) ? field : 'createdAt'
  
  // Validate sort direction
  const validDirection = direction === 'asc' ? 'asc' : 'desc'
  
  return {
    field: validField,
    direction: validDirection
  }
}

/**
 * Parse tags parameter into array
 * @param tagsParam - Comma-separated tags string
 * @returns Array of tags
 */
export function parseTagsParam(tagsParam?: string): string[] {
  if (!tagsParam) return []
  return tagsParam.split(',').map(tag => tag.trim()).filter(Boolean)
}

/**
 * Calculate pagination metadata
 * @param total - Total number of records
 * @param page - Current page number
 * @param limit - Number of records per page
 * @returns Pagination metadata
 */
export function calculatePaginationMeta(total: number, page: number, limit: number): PaginationMeta {
  const totalPages = Math.ceil(total / limit)
  
  return {
    total,
    page,
    limit,
    totalPages,
    hasNextPage: page < totalPages,
    hasPrevPage: page > 1
  }
}

/**
 * Response data for successful knowledge deletion
 */
export type KnowledgeDeleteData = {
  deletedId: string
  deletedTables: string[]
  deletedAt: string
  summary?: {
    knowledgeRecords: number
    contentRecords: number
    embeddingRecords: number
  }
}

/**
 * Success response for knowledge deletion endpoint
 */
export type KnowledgeDeleteResponse = {
  success: true
  message: string
  data: KnowledgeDeleteData
}

/**
 * Union type for all possible knowledge delete API responses
 */
export type KnowledgeDeleteApiResponse = KnowledgeDeleteResponse | KnowledgeErrorResponse

/**
 * Request schema for deleting knowledge content (query parameter)
 */
export const deleteKnowledgeContentQuerySchema = z.object({
  contentId: z.string().uuid('Invalid content ID format')
})

/**
 * Type for validated content deletion request
 */
export type DeleteKnowledgeContentQuery = z.infer<typeof deleteKnowledgeContentQuerySchema>

/**
 * Response data for successful knowledge content deletion
 */
export type KnowledgeContentDeleteData = {
  deletedContentId: string
  deletedEmbeddingIds: string[]
  deletedAt: string
  summary: {
    contentRecords: number
    embeddingRecords: number
  }
}

/**
 * Success response for knowledge content deletion endpoint
 */
export type KnowledgeContentDeleteResponse = {
  success: true
  message: string
  data: KnowledgeContentDeleteData
}

/**
 * Union type for all possible knowledge content delete API responses
 */
export type KnowledgeContentDeleteApiResponse = KnowledgeContentDeleteResponse | KnowledgeErrorResponse